import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils/classNames';
import Tooltip from '@/components/Tooltip';

/* ── Module-level keyboard shortcut registry ──────────────────────────────
 * Ctrl+[ / Ctrl+] move to the previous / next pagination page. A single
 * document listener handles every GgcPagination instance. When multiple
 * instances coexist on the same page the most-recently-mounted one wins. */
interface ShortcutHandler {
  prev: () => void;
  next: () => void;
}
const shortcutHandlers: ShortcutHandler[] = [];
let shortcutListenerInstalled = false;

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  return false;
}

function onShortcutKey(e: KeyboardEvent) {
  // Use only Ctrl (not Cmd) to avoid Mac browser back/forward (⌘+[, ⌘+])
  if (!e.ctrlKey || e.shiftKey || e.altKey || e.metaKey) return;
  if (e.key !== '[' && e.key !== ']') return;
  if (isTypingTarget(e.target)) return;
  const top = shortcutHandlers[shortcutHandlers.length - 1];
  if (!top) return;
  e.preventDefault();
  if (e.key === '[') top.prev();
  else top.next();
}

function installShortcutListener(): void {
  if (shortcutListenerInstalled || typeof document === 'undefined') return;
  shortcutListenerInstalled = true;
  document.addEventListener('keydown', onShortcutKey);
}

/** Human-readable label for the pagination shortcut. */
export const PAGINATION_SHORTCUT_LABEL = {
  prev: 'Ctrl+[',
  next: 'Ctrl+]',
} as const;

/**
 * GGC V2. Pagination — Figma node 166:252 family (Component 02).
 *
 * Numeric pagination with prev/next arrows. Active page swaps to the CTA
 * blue background. Renders a compact strip of buttons; consumers handle
 * trimming with ellipses when needed (`renderEllipsis`).
 *
 * Spec: button 32×32, r-md, gap 3px, font 10px (12px for arrows),
 * active bg cta-blue / white text.
 *
 * Use this for new GGC V2. screens. The legacy `@/components/ui/pagination`
 * remains for pages not yet migrated.
 */
export interface GgcPaginationProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 1-based current page. */
  page: number;
  /** Total number of pages (>= 1). */
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Maximum numeric buttons rendered between arrows. Default 5. */
  maxButtons?: number;
  /** i18n overrides for built-in aria labels. English defaults retained. */
  labels?: {
    prev?: string;
    next?: string;
    jump?: string;
  };
  /**
   * Show a "jump to page" dropdown when there are more pages than numeric
   * buttons. Default true — lets users reach any page directly instead of
   * clicking the arrows repeatedly.
   */
  showJump?: boolean;
}

function buildPageList(page: number, total: number, max: number): number[] {
  if (total <= max) return Array.from({ length: total }, (_, i) => i + 1);
  const half = Math.floor(max / 2);
  let start = Math.max(1, page - half);
  const end = Math.min(total, start + max - 1);
  start = Math.max(1, end - max + 1);
  const list: number[] = [];
  for (let i = start; i <= end; i += 1) list.push(i);
  return list;
}

const buttonClass =
  'inline-flex size-[25px] items-center justify-center rounded-ggc-md border bg-white font-ggc transition-colors disabled:cursor-not-allowed disabled:opacity-50';
const idleClass = 'border-ggc-disabled text-ggc-text-gray hover:border-ggc-cta-blue hover:text-ggc-cta-blue';
const activeClass = 'border-ggc-cta-blue bg-ggc-cta-blue text-white font-semibold';
const jumpSelectClass =
  'h-[26px] min-w-[30px] appearance-none rounded-ggc-md border border-ggc-disabled bg-white pl-2.5 pr-6 text-center text-[10px] font-ggc text-ggc-text-gray outline-none transition-colors cursor-pointer hover:border-ggc-cta-blue focus:border-ggc-cta-blue focus:text-ggc-cta-blue';

const GgcPagination = React.forwardRef<HTMLDivElement, GgcPaginationProps>(
  ({ className, page, totalPages, onPageChange, maxButtons = 5, labels, showJump = true, ...props }, ref) => {
    const pages = buildPageList(page, totalPages, maxButtons);
    const canPrev = page > 1;
    const canNext = page < totalPages;

    React.useEffect(() => {
      installShortcutListener();
      const handler: ShortcutHandler = {
        prev: () => { if (page > 1) onPageChange(page - 1); },
        next: () => { if (page < totalPages) onPageChange(page + 1); },
      };
      shortcutHandlers.push(handler);
      return () => {
        const idx = shortcutHandlers.indexOf(handler);
        if (idx >= 0) shortcutHandlers.splice(idx, 1);
      };
    }, [page, totalPages, onPageChange]);

    const prevLabel = labels?.prev ?? 'Previous page';
    const nextLabel = labels?.next ?? 'Next page';

    return (
      <div
        ref={ref}
        className={cn('inline-flex items-center justify-center gap-[3px]', className)}
        {...props}
      >
        <Tooltip content={`${prevLabel} (${PAGINATION_SHORTCUT_LABEL.prev})`}>
          <button
            type="button"
            aria-label={prevLabel}
            disabled={!canPrev}
            onClick={() => canPrev && onPageChange(page - 1)}
            className={cn(buttonClass, idleClass, 'text-[12px] font-medium')}
          >
            ←
          </button>
        </Tooltip>
        {pages.map((p) => (
          <button
            key={p}
            type="button"
            aria-current={p === page ? 'page' : undefined}
            onClick={() => onPageChange(p)}
            className={cn(buttonClass, p === page ? activeClass : idleClass, 'text-[10px]')}
          >
            {p}
          </button>
        ))}
        <Tooltip content={`${nextLabel} (${PAGINATION_SHORTCUT_LABEL.next})`}>
          <button
            type="button"
            aria-label={nextLabel}
            disabled={!canNext}
            onClick={() => canNext && onPageChange(page + 1)}
            className={cn(buttonClass, idleClass, 'text-[12px] font-medium')}
          >
            →
          </button>
        </Tooltip>
        {showJump && totalPages > maxButtons && (
          <span className="ml-1 inline-flex items-center gap-1">
            <span className="relative inline-flex items-center">
              <Tooltip content={labels?.jump ?? 'Go to page'}>
                <select
                  aria-label={labels?.jump ?? 'Go to page'}
                  value={page}
                  onChange={(e) => onPageChange(Number(e.target.value))}
                  className={jumpSelectClass}
                >
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </Tooltip>
              <ChevronDown
                className="pointer-events-none absolute right-1.5 top-1/2 size-3 -translate-y-1/2 text-ggc-text-gray"
                aria-hidden
              />
            </span>
            <span className="text-[10px] font-ggc text-ggc-text-gray">/ {totalPages}</span>
          </span>
        )}
      </div>
    );
  }
);
GgcPagination.displayName = 'GgcPagination';

export { GgcPagination };
